import * as AmplitudeBrowser from "@amplitude/analytics-browser";
import * as AmplitudeNative from "@amplitude/analytics-react-native";
import * as AuthSession from "expo-auth-session";
import Constants from "expo-constants";
import { isFunction } from "lodash";
import moment from "moment-mini";
import qs from "qs";
import Purchases from "react-native-purchases";
import { router } from "expo-router";
import { push } from "react-router-redux";
import { call, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import ApiClient from "../../../../api/ApiClient";
import authFormFieldsToRequestData from "../../../../lib/authFormFieldsToRequestData";
import { UrlProvider } from "../../../../api/UrlProvider";
import config from "../../../../config";
import AnalyticsEventTracker from "../../../../lib/AnalyticsEventTracker/";
import AmplitudeHelper from "../../../../lib/AnalyticsEventTracker/AmplitudeHelper";
import {
  clearRedirectToInSessionStorage,
  getRedirectToFromSessionStorage,
  hasRedirectToInSessionStorage,
  setRedirectToInSessionStorage
} from "../../../../lib/api/redirectSessionStorage";
import marketingParamsStorage from "../../../../lib/MarketingParams/MarketingParamsStorage";
import {
  isAlreadyAtRedirectTarget,
  isSafeAuthRedirectUrl,
  resolvePostAuthRedirectPath
} from "../../../../lib/auth/authRedirectUrl";
import appendLangToAuthRequestData from "../../../../lib/auth/appendLangToAuthRequestData";
import buildRedirectUrl from "../../../../lib/buildRedirectUrl";
import { getIsIos, getIsWeb, getSystem } from "../../../../lib/platformHelper";
import { resetToAuthOnboarding } from "../../../../native/lib/navigation";
import UserLoginEvent from "../../../../lib/SalesManago/events/UserLoginEvent";
import TagsHelper from "../../../../lib/TagsHelper/TagsHelper";
import { affiliate } from "../../affiliateProgram";
import { actions as alertsActions } from "../../alerts";
import { actions as menuActions } from "../../menu";
import { setAuthLoginLoading } from "../../authLoginLoading";
import { setInitialAuthCheckPending } from "../../initialAuthCheck";
import { clearFollowedStocks, followedStocksRoutine } from "../../followed";
import { fetchMarketingBarsRoutine } from "../../marketingBars";
import {
  clearMarketingMessages,
  fetchMarketingMessagesRoutine
} from "../../marketingMessages";
import { actions as notificationsActions } from "../../notifications";
import { clearPaymentDetails, paymentDetailsRoutine } from "../../payment";
import {
  clearPortfoliosList,
  getPortfoliosListRoutine
} from "../../portfolios/index";
import { trackSMEvent } from "../../salesManagoEventTracker";
import { setUserDataUpdateError } from "../../status";
import { updateStocksListSource } from "../../tags";
import { initTagsServiceRoutine } from "../../tagsService";
import { initTagsService } from "../../tagsService/saga";
import { getAllTags } from "../../tagsService/selector";
import { getLocale } from "../../translations/selector";
import { actions, constants, SOCIAL_LOGIN_SITES } from "./index";
import { getPushNotificationsToken, getUserData, getUserID } from "./selector";
import {
  clearNotifications,
  fetchNotifications as fetchNotificationsNew
} from "../../newNotifications";
import { clearInvoices } from "../../invoices";

let Cookies;

if (getIsWeb()) {
  Cookies = require("js-cookie");
}

function getUserAccessLevel(userData) {
  return userData.has_access ? "premium" : "freemium";
}

function* onLogin({ payload: { data } }) {
  const requestData = authFormFieldsToRequestData(data);

  try {
    yield put(setAuthLoginLoading(true));
    const { data } = yield ApiClient.post({
      urlPath: "app.auth.login",
      data: requestData
    });

    const { token } = data;

    yield afterLogin(token);
  } catch (error) {
    console.log(error);
    const response = JSON.parse(error.request.response);
    yield put(actions.loginError());
    yield put(
      alertsActions.addErrorAlert({
        title: response.message
      })
    );
    yield put(setAuthLoginLoading(false));
  }
}

export function* afterLogin(
  token,
  afterRegister = false,
  cookieLogin = false,
  registerSource: string = null,
  displayUserNameAfterLogin: boolean = false,
  registrationMethod: string = null
) {
  let searchParams = {};
  const locale = yield select(getLocale);

  if (getIsWeb()) {
    const { location } = require("../../../configureStore").history;

    searchParams = qs.parse(location.search, {
      ignoreQueryPrefix: true
    });
  }

  yield put(actions.setAuthToken(token));

  yield initTagsService();
  const allTags = yield select(getAllTags);
  const wig20Tag = allTags.find(tag => TagsHelper.isDefaultTag(tag));
  const alertedTag = allTags.find(tag => TagsHelper.isAlertedTag(tag));

  const { data: userData } = yield ApiClient.get("app.auth.user");

  if (getIsWeb()) {
    if (window.gtag) {
      gtag("set", "user_data", {
        email: userData.email
      });
    }
  }

  const userHasAccess = userData.has_access;

  if (afterRegister) {
    yield AnalyticsEventTracker.trackRegisterConversion(
      registerSource,
      userData.id,
      registrationMethod
    );
  }

  if (!getIsWeb()) {
    try {
      yield AnalyticsEventTracker.setFirebaseUserId(String(userData.id));
    } catch (error) {}
  }

  yield put(actions.loginSuccess(userData));
  yield put(setAuthLoginLoading(false));

  yield put(menuActions.fetchMenu());
  yield put(fetchNotificationsNew.trigger());
  yield put(followedStocksRoutine());
  yield put(getPortfoliosListRoutine());
  yield put(paymentDetailsRoutine());
  yield put(fetchMarketingBarsRoutine());
  yield put(fetchMarketingMessagesRoutine());
  yield put(actions.setUserWasLoggedIn(true));
  if (!displayUserNameAfterLogin) {
    yield put(
      alertsActions.addSuccessAlert({
        title: "Login successful"
      })
    );
  } else {
    yield put(
      alertsActions.addSuccessAlert({
        title: "You are now logged in as %{userName}",
        translationVars: {
          userName: userData.username
        }
      })
    );
  }

  if (!afterRegister) {
    const smEvent = new UserLoginEvent({
      loginType: registrationMethod || "email",
      referrer: getIsWeb() ? document.referrer : null,
      redirectionAfterLogin: searchParams?.authRedirect || null
    });

    yield put(trackSMEvent(smEvent));
  }

  if (getIsWeb()) {
    Cookies.set(config.userIdCookieName, userData.id, {
      domain: config.cookieDomain
    });
    Cookies.set(config.authTokenCookieName, token, {
      domain: config.cookieDomain
    });

    if (
      searchParams.authRedirect &&
      isSafeAuthRedirectUrl(searchParams.authRedirect)
    ) {
      yield put(actions.executeAuthRedirect(searchParams.authRedirect));
    } else if (!cookieLogin) {
      // Do not execute redirect if login was triggered by auth cookie
      let redirectUrl = UrlProvider.getUrl("fe.desktopPage", { locale });

      if (!userHasAccess) {
        redirectUrl = UrlProvider.getUrl("fe.desktopPageWithTagId", {
          locale,
          tagId: wig20Tag.id
        });
      }

      const persistedSocialRedirectTo = hasRedirectToInSessionStorage()
        ? getRedirectToFromSessionStorage()
        : null;

      const redirectParams = persistedSocialRedirectTo
        ? {
            ...searchParams,
            redirectTo: searchParams.redirectTo || persistedSocialRedirectTo
          }
        : searchParams;

      redirectUrl = resolvePostAuthRedirectPath(redirectParams, redirectUrl);

      clearRedirectToInSessionStorage();

      // Skip the hard reload if a client-side redirect (e.g. NewLoginPage)
      // already landed on this exact URL.
      if (!isAlreadyAtRedirectTarget(redirectUrl)) {
        window.location.href = redirectUrl;
      }
    }

    if (cookieLogin && searchParams.auth_token_for_login) {
      if (searchParams.redirectTo) {
        const redirectUrl = resolvePostAuthRedirectPath(
          searchParams,
          window.location.pathname
        );

        window.location.replace(redirectUrl);
        return;
      }

      let redirectUrl = window.location.pathname;

      yield put(push(redirectUrl));
    }
  } else {
    if (!userHasAccess || !alertedTag || !alertedTag.stocks.length) {
      yield put(updateStocksListSource(wig20Tag));
    } else if (alertedTag && alertedTag.stocks.length) {
      yield put(updateStocksListSource(alertedTag));
    }

    AmplitudeNative.setUserId(String(userData.id));
  }

  try {
    AnalyticsEventTracker.trackUserLogInEvent();
  } catch (e) {}
}

function* onCookieLogin({ payload: { token, indicateChangingAccount } }) {
  yield put(setAuthLoginLoading(true));

  try {
    yield afterLogin(token, false, true, null, indicateChangingAccount);
  } finally {
    if (getIsWeb()) {
      yield put(setInitialAuthCheckPending(false));
    }
  }
}

function* onAutoLogin({ payload: { token } }) {
  try {
    const { data } = yield ApiClient.post({
      urlPath: "app.auth.login",
      data: {
        access_token: token
      }
    });

    const { token } = data;

    yield afterLogin(token);
  } catch (error) {
    yield put(
      alertsActions.addErrorAlert({
        title: "Auto login failed"
      })
    );

    const locale = yield select(getLocale);
    yield put(push(UrlProvider.getUrl("fe.login", { locale })));
  }
}

function* onRefreshUserData({ payload = {} }) {
  const { skipAffiliation, skipSettingGtmUserData, callback } = payload;

  let shouldProceed = false;

  let userData = null;

  try {
    const { data: userDataResponse } = yield ApiClient.get({
      urlPath: "app.auth.user",
      skipAccessCheck: true
    });

    if (getIsWeb() && !skipSettingGtmUserData) {
      if (window.gtag) {
        gtag("set", "user_data", {
          email: userDataResponse.email
        });
      }
    }

    userData = userDataResponse;

    if (userData) {
      yield put(actions.loginSuccess(userData));
      yield put(actions.refreshUserData.success(true));

      if (!skipAffiliation) {
        yield put(affiliate());
      }

      if (callback && isFunction(callback)) {
        yield callback(userData);
      }
    }

    shouldProceed = true;
  } catch (error) {
    if (error && error.request) {
      const { status } = error.request;
      if (status === 401 || status === 403) {
        yield put(actions.logout());
        yield put(actions.refreshUserData.failure(false));
      } else if (status >= 500) {
        yield put(setUserDataUpdateError(true));
      }
    }

    if (callback && isFunction(callback)) {
      yield callback(null);
    }
  }

  if (!shouldProceed) {
    return;
  }

  const user_status = userData.has_access ? "premium" : "freemium";
  const registration_date = moment(userData.date_joined).format("DD-MM-YYYY");

  const {
    email_validated: confirmed,
    email_marketing: marketing_emails,
    electronic_communication
  } = userData;

  const userDataToApply = {
    user_status,
    registration_date,
    confirmed,
    marketing_emails,
    electronic_communication
  };

  yield put(actions.setUserWasLoggedIn(true));

  if (getIsWeb()) {
    const newAccessLevel = getUserAccessLevel(userData);
    const oldAccessLevel = localStorage.getItem(
      config.oldAccessLevelStorageKey
    );
    const paymentDetails = localStorage.getItem(
      config.paymentDetailsStorageKey
    );

    if (oldAccessLevel !== newAccessLevel && paymentDetails) {
      const paymentDetailsObject = JSON.parse(paymentDetails);

      AnalyticsEventTracker.trackPaymentConversion(paymentDetailsObject);

      localStorage.removeItem(config.paymentDetailsStorageKey);
    }

    localStorage.setItem(config.oldAccessLevelStorageKey, newAccessLevel);

    if (userData) {
      Cookies.set(config.userIdCookieName, userData.id, {
        domain: config.cookieDomain
      });

      const identify = AmplitudeHelper.getIdentify(
        AmplitudeBrowser.Identify,
        userDataToApply
      );

      AmplitudeBrowser.identify(identify);
    }
  } else {
    if (userData) {
      try {
        const identify = AmplitudeHelper.getIdentify(
          AmplitudeNative.Identify,
          userDataToApply
        );

        yield AmplitudeNative.identify(identify);
      } catch (error) {
        console.log("ERROR at Amplitude.setUserPropertiesAsync");
        console.log(error);
      }

      try {
        yield AnalyticsEventTracker.setFirebaseUserId(String(userData.id));
      } catch (error) {}
    }
  }
}

function* onSocialLogin({ payload: { site } }) {
  if (!SOCIAL_LOGIN_SITES[site]) {
    throw new Error(
      `Site can be one of: [${Object.keys(SOCIAL_LOGIN_SITES)
        .map(item => `"${item}"`)
        .join(", ")}]. Received "${site}" instead`
    );
  }

  let socialLoginSite = SOCIAL_LOGIN_SITES[site];

  if (!getIsWeb()) {
    socialLoginSite = `${socialLoginSite}_native`;
  }

  try {
    const {
      data: { redirect_url: redirectUrl }
    } = yield ApiClient.get(`app.auth.${socialLoginSite}`);

    if (getIsWeb()) {
      const { search } = window.location;
      const { redirectTo } = qs.parse(search, {
        ignoreQueryPrefix: true
      });

      setRedirectToInSessionStorage(redirectTo);
      window.location.href = redirectUrl;
    } else {
      const appScheme =
        Constants.expoConfig.scheme || config.expoSchemeFallback;

      const result = yield AuthSession.startAsync({
        authUrl: redirectUrl,
        returnUrl: `${appScheme}://expo-auth-session`,
        projectNameForProxy: config.expoProjectNameForProxy
      });

      if (result.type === "cancel" || result.type === "dismissed") {
        yield put(
          alertsActions.addInfoAlert({
            title: "Canceled"
          })
        );
      } else if (result.type === "success") {
        const { params } = result;

        const processedResult = {
          access_token: params.access_token,
          expires_in: params.expires_in,
          social_type_id: params.social_type_id,
          token_type: params.token_type,
          site
        };

        yield onExecuteSocialLogin({
          payload: { data: processedResult }
        });
      } else {
        yield put(
          alertsActions.addErrorAlert({
            title: "Unexpected error occurred. Please contact support."
          })
        );
      }
    }
  } catch (error) {
    let response = "";

    if (error?.request?.response) {
      response = JSON.parse(error.request.response);
    }

    if (response?.message) {
      yield put(
        alertsActions.addErrorAlert({
          title: response.message
        })
      );
    } else {
      alertsActions.addErrorAlert({
        title: "Unexpected error occurred. Please contact support."
      });
    }
  }
}

export function* onExecuteSocialLogin({ payload: { data } }) {
  const { site, social_type_id } = data;
  const registerSource = site === "FACEBOOK" ? "facebook" : "gmail";
  let socialLoginSource = registerSource;

  if (social_type_id === 5) {
    socialLoginSource = "apple";
  }

  try {
    const {
      data: { token }
    } = yield ApiClient.post({
      urlPath: "app.auth.socialLogin",
      data
    });

    yield call(afterLogin, token, false, false, null, false, socialLoginSource);
  } catch (error) {
    if (error?.request?.status === 500) {
      yield put(
        alertsActions.addErrorAlert({
          title:
            "Unexpected error occurred. Contact support for more information."
        })
      );

      return;
    }

    const response = error?.request?.response
      ? JSON.parse(error.request.response)
      : {};

    if (response.validation) {
      yield put(
        alertsActions.addErrorAlert({
          title: response.message
        })
      );

      return;
    }

    try {
      const locale = yield select(getLocale);
      const {
        data: { token }
      } = yield ApiClient.post({
        urlPath: "app.auth.socialRegister",
        data: appendLangToAuthRequestData(data, locale)
      });

      yield call(afterLogin, token, true, false, "Social", false, registerSource);
    } catch (registerError) {
      const registerResponse = registerError?.request?.response
        ? JSON.parse(registerError.request.response)
        : {};

      yield put(
        alertsActions.addErrorAlert({
          title:
            registerResponse?.message ||
            "Unexpected error occurred. Contact support for more information."
        })
      );
    }
  }
}

export function* onExecuteSocialLoginV2({ payload: { data } }) {
  const socialLoginSource = "gmail";

  try {
    const {
      data: { token }
    } = yield ApiClient.post({
      urlPath: "app.auth.googleV2Login",
      data
    });

    yield afterLogin(token, false, false, null, false, socialLoginSource);
  } catch (error) {
    if (error?.request?.status === 500) {
      yield put(
        alertsActions.addErrorAlert({
          title:
            "Unexpected error occurred. Contact support for more information."
        })
      );

      return;
    }

    try {
      const locale = yield select(getLocale);
      const {
        data: { token }
      } = yield ApiClient.post({
        urlPath: "app.auth.googleV2Register",
        data: appendLangToAuthRequestData(data, locale)
      });

      yield afterLogin(
        token,
        true,
        false,
        socialLoginSource,
        false,
        socialLoginSource
      );
    } catch (e) {
      const response = error?.request?.response
        ? JSON.parse(error.request.response)
        : {};

      yield put(
        alertsActions.addErrorAlert({
          title:
            response?.message ||
            "Google sign-in failed. Contact support for more information."
        })
      );
    }
  }
}

function* onLogout() {
  const locale = yield select(getLocale);
  const pushNotificationsToken = yield select(getPushNotificationsToken);
  const userId = yield select(getUserID);

  const allTags = yield select(getAllTags);
  const wig20Tag = allTags.find(tag => TagsHelper.isDefaultTag(tag));
  yield put(updateStocksListSource(wig20Tag));

  marketingParamsStorage.clear();

  if (getIsWeb()) {
    Cookies.remove(config.authTokenCookieName, {
      domain: config.cookieDomain
    });
    Cookies.remove(config.userIdCookieName, {
      domain: config.cookieDomain
    });

    AmplitudeBrowser.identify(new AmplitudeBrowser.Identify().clearAll());
    AmplitudeBrowser.setUserId(null);
  } else {
    yield unregisterFromPushNotifications({
      payload: {
        registrationId: pushNotificationsToken,
        userId
      }
    });

    try {
      AmplitudeNative.setUserId(null);
      AmplitudeNative.reset();
    } catch (error) {
      console.log("ERROR at Amplitude.reset or Amplitude.setUserId");
      console.log(error);
    }

    try {
      yield AnalyticsEventTracker.setFirebaseUserId(null);
    } catch (error) {}

    if (getIsIos()) {
      try {
        yield Purchases.logOut();
      } catch (e) {}
    }
  }

  yield put(actions.logoutAfter());

  if (getIsWeb()) {
    setTimeout(() => {
      window.location.href = UrlProvider.getUrl("fe.login", { locale });
    }, 1000);
  } else {
    resetToAuthOnboarding();
  }
}

function* onLogoutAfter() {
  yield put(notificationsActions.clearNotifications());
  yield put(clearFollowedStocks());
  yield put(clearPaymentDetails());
  yield put(clearPortfoliosList());
  yield put(clearNotifications());
  yield put(clearInvoices());
  yield put(clearMarketingMessages());
  yield put(initTagsServiceRoutine());
}

function* onRegisterForPushNotifications({ payload }) {
  const system = getSystem();
  const urlPath = `app.pushNotifications.${system}`;

  try {
    yield ApiClient.post({
      urlPath,
      data: {
        user: payload.userId,
        registration_id: payload.registrationId
      }
    });

    yield put(
      actions.registerForPushNotificationsSuccess(payload.registrationId)
    );
  } catch (error) {
    console.log(error.request);
  }
}

function* unregisterFromPushNotifications({ payload }) {
  const system = getSystem();

  const { registrationId, userId } = payload;

  if (!registrationId) {
    return;
  }

  const urlPath = `app.pushNotifications.${system}`;

  try {
    yield ApiClient.delete({
      urlPath,
      data: {
        user: userId,
        registration_id: registrationId
      },
      skipAccessCheck: true
    });
  } catch (error) {
    console.log(error);
  }
}

function* onEditUserData({ payload }) {
  let requestData = {};

  payload.userData.forEach(field => {
    requestData[field.name] = field.value;
  });

  try {
    const { data } = yield ApiClient.put({
      urlPath: "app.auth.editUserData",
      data: requestData
    });

    yield put(actions.refreshUserData());
    yield put(
      alertsActions.addSuccessAlert({
        title: data.message
      })
    );
    payload.callback();
  } catch (error) {
    if (error.request) {
      const response = JSON.parse(error.request.response);
      yield put(actions.editUserDataError(response.validation));
      yield put(
        alertsActions.addErrorAlert({
          title: response.message
        })
      );
    }
  }
}

function* onEditUserPassword({ payload }) {
  let requestData = {};

  payload.formData.forEach(field => {
    requestData[field.name] = field.value;
  });

  try {
    const { data } = yield ApiClient.put({
      urlPath: "app.auth.editUserData",
      data: requestData
    });

    yield put(actions.refreshUserData());
    yield put(
      alertsActions.addSuccessAlert({
        title: data.message
      })
    );
    payload.form.reset();
  } catch (error) {
    if (error.request) {
      const response = JSON.parse(error.request.response);
      yield put(actions.editUserPasswordError(response.validation));
      yield put(
        alertsActions.addErrorAlert({
          title: response.message
        })
      );
    }
  }
}

function* onToggleMarketingEmails() {
  const { marketing_emails } = yield select(getUserData);

  try {
    const { data } = yield ApiClient.put({
      urlPath: "app.auth.editUserData",
      data: { marketing_emails }
    });

    yield put(
      alertsActions.addSuccessAlert({
        title: data.message
      })
    );
  } catch (error) {
    if (error.request) {
      const response = JSON.parse(error.request.response);
      yield put(
        alertsActions.addErrorAlert({
          title: response.message
        })
      );
    }
  }
}

export function* executeAuthRedirect({ payload: { authRedirect } }) {
  if (!isSafeAuthRedirectUrl(authRedirect)) {
    return;
  }

  const userData = yield select(getUserData);

  try {
    const { data } = yield ApiClient.get("app.auth.loginToken");

    const redirectUrl = buildRedirectUrl(
      authRedirect,
      data.login_token,
      userData.id
    );

    window.location.replace(redirectUrl);
  } catch (error) {
    console.log(error);
  }
}

function* watchAuthActions() {
  yield takeLatest(constants.LOGIN, onLogin);
  yield takeLatest(constants.SOCIAL_LOGIN, onSocialLogin);
  yield takeLatest(constants.EXECUTE_SOCIAL_LOGIN, onExecuteSocialLogin);
  yield takeLatest(constants.EXECUTE_SOCIAL_LOGIN_V2, onExecuteSocialLoginV2);
  yield takeLatest(
    constants.REGISTER_FOR_PUSH_NOTIFICATIONS,
    onRegisterForPushNotifications
  );
  yield takeEvery(actions.refreshUserData.TRIGGER, onRefreshUserData); // We use takeEvery here because onRefreshUserData support callback. If we use takeLast callback can be skipped
  yield takeLatest(constants.COOKIE_LOGIN, onCookieLogin);
  yield takeLatest(constants.AUTO_LOGIN, onAutoLogin);

  yield takeLatest(constants.LOGOUT, onLogout);
  yield takeLatest(constants.LOGOUT_AFTER, onLogoutAfter);

  yield takeLatest(constants.EDIT_USER_DATA, onEditUserData);
  yield takeLatest(constants.EDIT_USER_PASSWORD, onEditUserPassword);
  yield takeLatest(constants.TOGGLE_MARKETING_EMAILS, onToggleMarketingEmails);

  yield takeLatest(constants.EXECUTE_AUTH_REDIRECT, executeAuthRedirect);

  yield takeLatest(
    constants.UNREGISTER_FROM_PUSH_NOTIFICATIONS,
    unregisterFromPushNotifications
  );
}

export default [fork(watchAuthActions)];
